import React from "react";
import styled from "styled-components";
import type { BoxProps } from "@xsolla/xui-primitives-core";
import { createFilteredElement } from "./filterDOMProps";

const FilteredDiv = createFilteredElement("div");

const StyledBox = styled(FilteredDiv)<BoxProps>`
  display: flex;
  box-sizing: border-box;
  background-color: ${(props) => props.backgroundColor || "transparent"};
  border-color: ${(props) => props.borderColor || "transparent"};
  border-width: ${(props) =>
    typeof props.borderWidth === "number"
      ? `${props.borderWidth}px`
      : props.borderWidth || 0};

  ${(props) =>
    props.borderBottomWidth !== undefined &&
    `
    border-bottom-width: ${typeof props.borderBottomWidth === "number" ? `${props.borderBottomWidth}px` : props.borderBottomWidth};
    border-bottom-color: ${props.borderBottomColor || props.borderColor || "transparent"};
    border-bottom-style: solid;
  `}
  ${(props) =>
    props.borderTopWidth !== undefined &&
    `
    border-top-width: ${typeof props.borderTopWidth === "number" ? `${props.borderTopWidth}px` : props.borderTopWidth};
    border-top-color: ${props.borderTopColor || props.borderColor || "transparent"};
    border-top-style: solid;
  `}
  ${(props) =>
    props.borderLeftWidth !== undefined &&
    `
    border-left-width: ${typeof props.borderLeftWidth === "number" ? `${props.borderLeftWidth}px` : props.borderLeftWidth};
    border-left-color: ${props.borderLeftColor || props.borderColor || "transparent"};
    border-left-style: solid;
  `}
  ${(props) =>
    props.borderRightWidth !== undefined &&
    `
    border-right-width: ${typeof props.borderRightWidth === "number" ? `${props.borderRightWidth}px` : props.borderRightWidth};
    border-right-color: ${props.borderRightColor || props.borderColor || "transparent"};
    border-right-style: solid;
  `}

  border-style: ${(props) =>
    props.borderStyle ||
    (props.borderWidth ||
    props.borderBottomWidth ||
    props.borderTopWidth ||
    props.borderLeftWidth ||
    props.borderRightWidth
      ? "solid"
      : "none")};
  border-radius: ${(props) =>
    typeof props.borderRadius === "number"
      ? `${props.borderRadius}px`
      : props.borderRadius || 0};
  height: ${(props) =>
    typeof props.height === "number"
      ? `${props.height}px`
      : props.height || "auto"};
  width: ${(props) =>
    typeof props.width === "number"
      ? `${props.width}px`
      : props.width || "auto"};
  min-width: ${(props) =>
    typeof props.minWidth === "number"
      ? `${props.minWidth}px`
      : props.minWidth || "auto"};
  min-height: ${(props) =>
    typeof props.minHeight === "number"
      ? `${props.minHeight}px`
      : props.minHeight || "auto"};
  max-width: ${(props) =>
    typeof props.maxWidth === "number"
      ? `${props.maxWidth}px`
      : props.maxWidth || "none"};
  max-height: ${(props) =>
    typeof props.maxHeight === "number"
      ? `${props.maxHeight}px`
      : props.maxHeight || "none"};

  padding: ${(props) =>
    typeof props.padding === "number"
      ? `${props.padding}px`
      : props.padding || 0};
  ${(props) =>
    props.paddingHorizontal &&
    `
    padding-left: ${typeof props.paddingHorizontal === "number" ? `${props.paddingHorizontal}px` : props.paddingHorizontal};
    padding-right: ${typeof props.paddingHorizontal === "number" ? `${props.paddingHorizontal}px` : props.paddingHorizontal};
  `}
  ${(props) =>
    props.paddingVertical &&
    `
    padding-top: ${typeof props.paddingVertical === "number" ? `${props.paddingVertical}px` : props.paddingVertical};
    padding-bottom: ${typeof props.paddingVertical === "number" ? `${props.paddingVertical}px` : props.paddingVertical};
  `}
  ${(props) =>
    props.paddingTop !== undefined &&
    `padding-top: ${typeof props.paddingTop === "number" ? `${props.paddingTop}px` : props.paddingTop};`}
  ${(props) =>
    props.paddingBottom !== undefined &&
    `padding-bottom: ${typeof props.paddingBottom === "number" ? `${props.paddingBottom}px` : props.paddingBottom};`}
  ${(props) =>
    props.paddingLeft !== undefined &&
    `padding-left: ${typeof props.paddingLeft === "number" ? `${props.paddingLeft}px` : props.paddingLeft};`}
  ${(props) =>
    props.paddingRight !== undefined &&
    `padding-right: ${typeof props.paddingRight === "number" ? `${props.paddingRight}px` : props.paddingRight};`}

  margin: ${(props) =>
    typeof props.margin === "number" ? `${props.margin}px` : props.margin || 0};
  ${(props) =>
    props.marginTop !== undefined &&
    `margin-top: ${typeof props.marginTop === "number" ? `${props.marginTop}px` : props.marginTop};`}
  ${(props) =>
    props.marginBottom !== undefined &&
    `margin-bottom: ${typeof props.marginBottom === "number" ? `${props.marginBottom}px` : props.marginBottom};`}
  ${(props) =>
    props.marginLeft !== undefined &&
    `margin-left: ${typeof props.marginLeft === "number" ? `${props.marginLeft}px` : props.marginLeft};`}
  ${(props) =>
    props.marginRight !== undefined &&
    `margin-right: ${typeof props.marginRight === "number" ? `${props.marginRight}px` : props.marginRight};`}

  flex-direction: ${(props) => props.flexDirection || "column"};
  flex-wrap: ${(props) => props.flexWrap || "nowrap"};
  align-items: ${(props) => props.alignItems || "stretch"};
  justify-content: ${(props) => props.justifyContent || "flex-start"};
  cursor: ${(props) =>
    props.cursor
      ? props.cursor
      : props.onClick || props.onPress
        ? "pointer"
        : "inherit"};
  position: ${(props) => props.position || "static"};
  top: ${(props) =>
    typeof props.top === "number" ? `${props.top}px` : props.top};
  bottom: ${(props) =>
    typeof props.bottom === "number" ? `${props.bottom}px` : props.bottom};
  left: ${(props) =>
    typeof props.left === "number" ? `${props.left}px` : props.left};
  right: ${(props) =>
    typeof props.right === "number" ? `${props.right}px` : props.right};
  flex: ${(props) => props.flex};
  flex-shrink: ${(props) => props.flexShrink ?? 1};
  gap: ${(props) =>
    typeof props.gap === "number" ? `${props.gap}px` : props.gap || 0};
  align-self: ${(props) => props.alignSelf || "auto"};
  /* Only emit overflow-x/y when explicitly set. Defaulting them to
     visible after overflow would override the shorthand and break
     clipping (e.g. OtherCard bottom-right promo image). */
  overflow: ${(props) => props.overflow || "visible"};
  ${(props) =>
    props.overflowX != null ? `overflow-x: ${props.overflowX};` : ""}
  ${(props) =>
    props.overflowY != null ? `overflow-y: ${props.overflowY};` : ""}
  z-index: ${(props) => props.zIndex};
  opacity: ${(props) =>
    props.opacity != null ? props.opacity : props.disabled ? 0.5 : 1};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};

  &:hover {
    ${(props) =>
      props.hoverStyle?.backgroundColor &&
      `background-color: ${props.hoverStyle.backgroundColor};`}
    ${(props) =>
      props.hoverStyle?.borderColor &&
      `border-color: ${props.hoverStyle.borderColor};`}
  }

  &:active {
    ${(props) =>
      props.pressStyle?.backgroundColor &&
      `background-color: ${props.pressStyle.backgroundColor};`}
  }
`;

export const Box = React.forwardRef<
  HTMLDivElement | HTMLButtonElement,
  BoxProps
>(
  (
    {
      children,
      onPress,
      onKeyDown,
      onKeyUp,
      role,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      "aria-current": ariaCurrent,
      "aria-disabled": ariaDisabled,
      "aria-live": ariaLive,
      "aria-busy": ariaBusy,
      "aria-describedby": ariaDescribedBy,
      "aria-expanded": ariaExpanded,
      "aria-haspopup": ariaHasPopup,
      "aria-pressed": ariaPressed,
      "aria-controls": ariaControls,
      tabIndex,
      as,
      src,
      alt,
      onError,
      onLoad,
      type,
      disabled,
      id,
      testID,
      "data-testid": dataTestId,
      ...props
    },
    ref
  ) => {
    // Handle as="img" for rendering images with proper border-radius
    if (as === "img" && src) {
      return (
        <img
          src={src}
          alt={alt || ""}
          onError={onError}
          onLoad={onLoad}
          data-testid={dataTestId || testID}
          style={{
            display: "block",
            objectFit: "cover",
            width:
              typeof props.width === "number"
                ? `${props.width}px`
                : props.width,
            height:
              typeof props.height === "number"
                ? `${props.height}px`
                : props.height,
            borderRadius:
              typeof props.borderRadius === "number"
                ? `${props.borderRadius}px`
                : props.borderRadius,
            position: props.position,
            top: typeof props.top === "number" ? `${props.top}px` : props.top,
            left:
              typeof props.left === "number" ? `${props.left}px` : props.left,
            right:
              typeof props.right === "number"
                ? `${props.right}px`
                : props.right,
            bottom:
              typeof props.bottom === "number"
                ? `${props.bottom}px`
                : props.bottom,
            ...props.style,
          }}
        />
      );
    }

    return (
      <StyledBox
        ref={ref}
        elementType={as}
        id={id}
        type={as === "button" ? type || "button" : undefined}
        disabled={as === "button" ? disabled : undefined}
        onClick={onPress}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        role={role}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-current={ariaCurrent}
        aria-disabled={ariaDisabled}
        aria-busy={ariaBusy}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-pressed={ariaPressed}
        aria-controls={ariaControls}
        aria-live={ariaLive}
        tabIndex={tabIndex !== undefined ? tabIndex : undefined}
        data-testid={dataTestId || testID}
        {...props}
      >
        {children}
      </StyledBox>
    );
  }
);

Box.displayName = "Box";
